/**
 * ClosingCostsForm - Step 4 of CD Creation Wizard
 *
 * The most complex form - manages all closing cost line items organized by RESPA sections:
 * - A: Origination Charges
 * - B: Services Borrower Did Not Shop For
 * - C: Services Borrower Did Shop For
 * - E: Taxes and Other Government Fees
 * - F: Prepaids
 * - G: Initial Escrow Payment at Closing
 * - H: Other
 *
 * Layout: Left (2/3) section tabs with tables, Right (1/3) sticky totals sidebar
 */

import { Loader2, PenLine } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  selectServerLineItems,
  useClosingDisclosureStore,
} from '../../store/closingDisclosureStore';
import type { CalculatedTotals, LineItem, LineItemSection } from '../../types';
import { formatCurrency } from '../../utils';
import { calculateSectionTotals, LineItemTable, SECTION_HEADERS } from './shared';

/** Section definitions */
const CD_SECTIONS: { id: LineItemSection; title: string; group: 'loan' | 'other' }[] = [
  { id: 'A', title: 'Origination Charges', group: 'loan' },
  { id: 'B', title: 'Services Borrower Did Not Shop For', group: 'loan' },
  { id: 'C', title: 'Services Borrower Did Shop For', group: 'loan' },
  { id: 'E', title: 'Taxes and Other Government Fees', group: 'other' },
  { id: 'F', title: 'Prepaids', group: 'other' },
  { id: 'G', title: 'Initial Escrow Payment at Closing', group: 'other' },
  { id: 'H', title: 'Other', group: 'other' },
];

interface ClosingCostsFormProps {
  lineItems?: LineItem[];
  calculations?: Partial<CalculatedTotals>;
  transactionId?: string;
  onNext: (data: { lineItems: LineItem[] }) => void;
  onBack: () => void;
  /** Optional callback to auto-save line items when switching sections */
  onSave?: (data: { lineItems: LineItem[] }) => Promise<void>;
}

/** Group line items by section */
function groupItemsBySection(items: LineItem[]): Map<LineItemSection, LineItem[]> {
  const grouped = new Map<LineItemSection, LineItem[]>();
  CD_SECTIONS.forEach((section) => {
    grouped.set(section.id, []);
  });
  items.forEach((item) => {
    const existing = grouped.get(item.section) ?? [];
    grouped.set(item.section, [...existing, item]);
  });
  return grouped;
}

/** Calculate totals for all sections */
function calculateAllTotals(itemsBySection: Map<LineItemSection, LineItem[]>) {
  let totalLoanCosts = 0;
  let totalOtherCosts = 0;
  let totalBorrowerAtClosing = 0;
  let totalBorrowerBefore = 0;
  let totalSellerAtClosing = 0;
  let totalSellerBefore = 0;
  let totalPaidByOthers = 0;

  CD_SECTIONS.forEach((section) => {
    const items = itemsBySection.get(section.id) ?? [];
    const sectionTotals = calculateSectionTotals(items);

    if (section.group === 'loan') {
      totalLoanCosts += sectionTotals.total;
    } else {
      totalOtherCosts += sectionTotals.total;
    }

    totalBorrowerAtClosing += sectionTotals.borrowerAtClosing;
    totalBorrowerBefore += sectionTotals.borrowerBeforeClosing;
    totalSellerAtClosing += sectionTotals.sellerAtClosing;
    totalSellerBefore += sectionTotals.sellerBeforeClosing;
    totalPaidByOthers += sectionTotals.paidByOthers;
  });

  return {
    totalLoanCosts,
    totalOtherCosts,
    totalClosingCosts: totalLoanCosts + totalOtherCosts,
    totalBorrowerAtClosing,
    totalBorrowerBefore,
    totalSellerAtClosing,
    totalSellerBefore,
    totalPaidByOthers,
  };
}

const ClosingCostsForm: React.FC<ClosingCostsFormProps> = ({
  lineItems: _initialLineItems = [],
  calculations: providedCalculations,
  transactionId = 'draft',
  onNext,
  onBack,
  onSave,
}) => {
  // Filter to only include sections A-H (closing costs), not L (seller credits)
  const closingCostSections = ['A', 'B', 'C', 'E', 'F', 'G', 'H'];

  // Read directly from store to ensure we always have the latest data
  // This bypasses any prop-passing timing issues
  const storeLineItems = useClosingDisclosureStore(selectServerLineItems);

  // Compute filtered items from store (prefer store, fallback to props)
  const closingCostsFromStore = useMemo(
    () => storeLineItems.filter((item) => closingCostSections.includes(item.section)),
    [storeLineItems],
  );

  // State for all line items
  // Note: With key={`step-${currentStep}`} on the wizard, this component remounts on step change
  // We use the store data directly as the source of truth
  const [allItems, setAllItems] = useState<LineItem[]>(closingCostsFromStore);

  // Keep local state in sync with store
  // This ensures we always reflect the latest saved data
  useEffect(() => {
    // Always sync from store when it has data
    // The store is the source of truth for persisted data
    if (closingCostsFromStore.length > 0) {
      setAllItems(closingCostsFromStore);
    }
  }, [closingCostsFromStore]);

  // Currently selected section tab
  const [activeSection, setActiveSection] = useState<LineItemSection>('A');

  // Group items by section
  const itemsBySection = useMemo(() => groupItemsBySection(allItems), [allItems]);

  // Calculate totals
  const totals = useMemo(() => calculateAllTotals(itemsBySection), [itemsBySection]);

  // Always use locally computed totals for display (they're calculated from current line items)
  // Only cashToClose comes from providedCalculations as it requires external data (loan amount, sale price)
  const displayCalculations = useMemo(() => {
    return {
      totalLoanCosts: totals.totalLoanCosts,
      totalOtherCosts: totals.totalOtherCosts,
      totalClosingCosts: totals.totalClosingCosts,
      cashToClose: providedCalculations?.cashToClose ?? 0,
    };
  }, [providedCalculations?.cashToClose, totals]);

  // Track if there are unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Handle section items change
  const handleSectionChange = useCallback((section: LineItemSection, sectionItems: LineItem[]) => {
    setAllItems((prev) => {
      // Remove old items from this section and add new ones
      const otherItems = prev.filter((item) => item.section !== section);
      return [...otherItems, ...sectionItems];
    });
    setHasUnsavedChanges(true);
  }, []);

  // Auto-save function
  const handleAutoSave = useCallback(async () => {
    if (!onSave || !hasUnsavedChanges || isSaving || transactionId === 'draft') return;

    setIsSaving(true);
    try {
      await onSave({ lineItems: allItems });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setIsSaving(false);
    }
  }, [onSave, hasUnsavedChanges, isSaving, transactionId, allItems]);

  // Save when switching sections
  const handleSectionSwitch = useCallback(
    async (newSection: LineItemSection) => {
      // Save current changes before switching
      await handleAutoSave();
      setActiveSection(newSection);
    },
    [handleAutoSave],
  );

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext({ lineItems: allItems });
  };

  // Get section totals for tab badges
  const getSectionTotal = (section: LineItemSection): number => {
    const items = itemsBySection.get(section) ?? [];
    return calculateSectionTotals(items).total;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Section Tabs and Tables */}
        <div className="lg:col-span-2">
          {/* Section Tabs */}
          <div className="flex flex-wrap gap-1 mb-4 border-b">
            {CD_SECTIONS.map((section) => {
              const sectionTotal = getSectionTotal(section.id);
              const hasItems = (itemsBySection.get(section.id) ?? []).length > 0;
              const isActive = activeSection === section.id;

              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => handleSectionSwitch(section.id)}
                  className={cn(
                    'px-3 py-2 text-sm font-medium border-b-2 transition-colors',
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span>{section.id}</span>
                    {hasItems && (
                      <span
                        className={cn(
                          'text-xs px-1.5 py-0.5 rounded-full',
                          isActive
                            ? 'bg-primary/20 text-primary'
                            : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {formatCurrency(sectionTotal)}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Section Group Headers */}
          <div className="mb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              {CD_SECTIONS.find((s) => s.id === activeSection)?.group === 'loan'
                ? 'Loan Costs (Section A-C)'
                : 'Other Costs (Section E-H)'}
            </p>
            <h3 className="text-lg font-semibold text-foreground">
              {SECTION_HEADERS[activeSection]}
            </h3>
          </div>

          {/* Line Item Table */}
          <LineItemTable
            section={activeSection}
            title={SECTION_HEADERS[activeSection]}
            items={itemsBySection.get(activeSection) ?? []}
            transactionId={transactionId}
            onChange={(items) => handleSectionChange(activeSection, items)}
          />

          {/* Section Navigation */}
          <div className="flex justify-between mt-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={activeSection === 'A' || isSaving}
              onClick={() => {
                const currentIndex = CD_SECTIONS.findIndex((s) => s.id === activeSection);
                const prevSection = CD_SECTIONS[currentIndex - 1];
                if (currentIndex > 0 && prevSection) {
                  handleSectionSwitch(prevSection.id);
                }
              }}
            >
              Previous Section
            </Button>
            <div className="flex items-center gap-2">
              {isSaving && (
                <Badge variant="outline" className="border-blue-500 text-blue-700 bg-blue-50">
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Saving...
                </Badge>
              )}
              {hasUnsavedChanges && !isSaving && (
                <Badge variant="outline" className="border-amber-500 text-amber-700 bg-amber-50">
                  <PenLine className="h-3 w-3 mr-1" />
                  Unsaved Changes
                </Badge>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={activeSection === 'H' || isSaving}
              onClick={() => {
                const currentIndex = CD_SECTIONS.findIndex((s) => s.id === activeSection);
                const nextSection = CD_SECTIONS[currentIndex + 1];
                if (currentIndex < CD_SECTIONS.length - 1 && nextSection) {
                  handleSectionSwitch(nextSection.id);
                }
              }}
            >
              Next Section
            </Button>
          </div>
        </div>

        {/* Totals Sidebar */}
        <div className="lg:col-span-1">
          <Card className="bg-muted sticky top-6">
            <CardHeader>
              <CardTitle className="text-lg">Closing Costs Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Section Breakdown */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">By Section</p>
                {CD_SECTIONS.map((section) => {
                  const sectionTotal = getSectionTotal(section.id);
                  return (
                    <div
                      key={section.id}
                      className="flex justify-between text-sm cursor-pointer hover:bg-accent px-2 py-1 rounded -mx-2"
                      onClick={() => handleSectionSwitch(section.id)}
                    >
                      <span className={cn(activeSection === section.id && 'font-medium')}>
                        {section.id}. {section.title}
                      </span>
                      <span className="font-medium">{formatCurrency(sectionTotal)}</span>
                    </div>
                  );
                })}
              </div>

              {/* Loan Costs vs Other Costs */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Loan Costs (A + B + C):</span>
                  <span className="font-semibold">
                    {formatCurrency(displayCalculations.totalLoanCosts)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Other Costs (E + F + G + H):</span>
                  <span className="font-semibold">
                    {formatCurrency(displayCalculations.totalOtherCosts)}
                  </span>
                </div>
              </div>

              {/* Total Closing Costs */}
              <div className="border-t pt-4">
                <div className="flex justify-between text-lg">
                  <span className="font-bold">Total Closing Costs:</span>
                  <span className="font-bold">
                    {formatCurrency(displayCalculations.totalClosingCosts)}
                  </span>
                </div>
              </div>

              {/* Payment Breakdown */}
              <div className="border-t pt-4 space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Paid By</p>
                <div className="flex justify-between text-sm">
                  <span>Borrower at Closing:</span>
                  <span className="font-medium">
                    {formatCurrency(totals.totalBorrowerAtClosing)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Borrower Before:</span>
                  <span className="font-medium">{formatCurrency(totals.totalBorrowerBefore)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Seller at Closing:</span>
                  <span className="font-medium">{formatCurrency(totals.totalSellerAtClosing)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Seller Before:</span>
                  <span className="font-medium">{formatCurrency(totals.totalSellerBefore)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Paid by Others:</span>
                  <span className="font-medium">{formatCurrency(totals.totalPaidByOthers)}</span>
                </div>
              </div>

              {/* Cash to Close */}
              {displayCalculations.cashToClose !== undefined && (
                <div className="border-t pt-4">
                  <div className="flex justify-between">
                    <span className="text-sm">Cash to Close:</span>
                    <span className="font-semibold text-lg text-green-600 dark:text-green-400">
                      {formatCurrency(displayCalculations.cashToClose)}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit">Next: Seller Credits</Button>
      </div>
    </form>
  );
};

export default ClosingCostsForm;
